import { offersBookings } from './bookings'
import { requestBannerCount } from './pendingRequests'
import { offersSignage } from './signage'
import { callingWaitSeconds } from './callingWait'
import type { PendingRequestView, Ticket } from './types'

export type TileKey =
  | 'counter'
  | 'reception'
  | 'floor'
  | 'bookings'
  | 'reports'
  | 'audit'
  | 'signage'
  | 'designs'
  | 'devices'
  | 'people'
  | 'administration'
  | 'organisations'

/** The colour a tile's mark is drawn in: one per kind of work. */
export type TileFamily = 'work' | 'insight' | 'screens' | 'setup'

export interface Tile {
  key: TileKey
  to: string
  family: TileFamily
}

export interface TileGate {
  permissions: readonly string[]
  modules: readonly string[] | undefined
  /** Whether Administration has any section for this person (pages/Administration.sectionsFor). */
  administer: boolean
}

/** The tiles a person may open, in the one fixed order: a menu that rearranges itself is never learnt. */
export function homeTiles({ permissions, modules, administer }: TileGate): Tile[] {
  const can = (p: string) => permissions.includes(p)
  const all: Array<Tile & { show: boolean }> = [
    { key: 'counter', to: '/counter', family: 'work', show: can('counter.operate') },
    { key: 'reception', to: '/reception', family: 'work', show: can('ticket.issue') },
    { key: 'bookings', to: '/appointments', family: 'work', show: can('appointment.manage') && offersBookings(modules, []) },
    { key: 'floor', to: '/floor', family: 'work', show: can('ticket.supervise') },
    { key: 'reports', to: '/reports', family: 'insight', show: can('report.read') },
    { key: 'audit', to: '/audit', family: 'insight', show: can('audit.read') },
    { key: 'signage', to: '/signage', family: 'screens', show: can('signage.manage') && offersSignage(modules) },
    { key: 'designs', to: '/screens', family: 'screens', show: can('layout.manage') },
    // Devices are set per branch, on the branch's own page.
    { key: 'devices', to: '/administration#branches', family: 'screens', show: can('branch.manage') },
    { key: 'people', to: '/people', family: 'setup', show: can('staff.manage') },
    { key: 'administration', to: '/administration', family: 'setup', show: administer },
    { key: 'organisations', to: '/administration#organisations', family: 'setup', show: can('tenant.manage') },
  ]
  return all.filter((t) => t.show).map(({ key, to, family }) => ({ key, to, family }))
}

// Pages that draw their own shell before asking the server; Settings, Reports and the
// Live dashboard say "no access" themselves.
const GUARDED = new Set(['/people', '/screens', '/signage', '/audit', '/appointments'])

/** Whether a person may open a page: a guarded page only when its tile is theirs. */
export function pageAllowed(path: string, tiles: readonly Tile[]): boolean {
  return !GUARDED.has(path) || tiles.some((t) => t.to === path)
}

/** Where a person landing on / goes instead of Home: their one app, when they have exactly one. */
export function landingPath(path: string, tiles: readonly Tile[]): string | undefined {
  return path === '/' && tiles.length === 1 ? tiles[0]!.to : undefined
}

/** The one-app skip, run once per sign-in: on the first screen after it, never again until sign-out. */
export function nextLanding(
  landed: boolean,
  identity: { mustChangePassword?: boolean } | null,
  path: string,
  tiles: () => readonly Tile[],
): { landed: boolean; to?: string } {
  if (!identity) return { landed: false }
  if (landed) return { landed: true }
  if (identity.mustChangePassword) return { landed: false }
  const to = landingPath(path, tiles())
  return to ? { landed: true, to } : { landed: true }
}

/** The greeting's part of the day, from the hour on the branch's clock. */
export function dayPart(hour: number): 'morning' | 'afternoon' | 'evening' {
  return hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening'
}

/** The name a greeting uses: the first word of the display name, else the username. */
export function firstName(displayName: string, username: string): string {
  return displayName.trim().split(/\s+/)[0] || username
}

export interface HomeFigures {
  waiting: number
  longestSec: number
  /** Queues with somebody past the target wait. */
  over: number
  /** Desk requests this person may decide. */
  requests: number
}

type Live = {
  queues: ReadonlyArray<{ waiting: number; longestWaitSec: number; breaching: number }>
  requests?: readonly PendingRequestView[]
  tickets?: readonly Ticket[]
}

/** The numbers Home shows, all from the live state it already holds. */
export function homeFigures(state: Live | null, permissions: readonly string[], viewerStaffId: string, now: number): HomeFigures {
  const queues = state?.queues ?? []
  // Measured against the clock, as the Floor's lines are; the queue's own figure is as old as the last message.
  const waiting = (state?.tickets ?? []).filter((t) => t.state === 'waiting')
  return {
    waiting: queues.reduce((n, q) => n + q.waiting, 0),
    longestSec: waiting.length
      ? Math.max(0, ...waiting.map((t) => callingWaitSeconds(t, now)))
      : Math.max(0, ...queues.map((q) => q.longestWaitSec)),
    over: queues.filter((q) => q.breaching > 0).length,
    requests: requestBannerCount(state?.requests, permissions, viewerStaffId),
  }
}

export interface TileBadge {
  n: number
  tone: 'plain' | 'over' | 'ask'
  what: 'waiting' | 'over' | 'requests'
}

/** A tile's numbers; none at zero. Requests sit on Floor because that is where they are decided. */
export function tileBadges(key: TileKey, f: HomeFigures): TileBadge[] {
  const out: TileBadge[] = []
  if (key === 'counter' && f.waiting > 0) out.push({ n: f.waiting, tone: 'plain', what: 'waiting' })
  if (key === 'floor' && f.over > 0) out.push({ n: f.over, tone: 'over', what: 'over' })
  if (key === 'floor' && f.requests > 0) out.push({ n: f.requests, tone: 'ask', what: 'requests' })
  return out
}
